import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { Backdrop, CircularProgress } from "@mui/material";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo from "../../assets/Ntact.png";
import ScorecardPopup from "./ScorecardPopup";

/**
 * Read-only subcontractor detail view styled to mirror the HSE-18
 * "Subcontractor Safety Pre-Qualification Form" PDF layout pixel-by-pixel.
 *
 * - On-screen: HTML modal using compact inline `Label: value` cells.
 * - Print button: generates a PDF via @react-pdf/renderer and opens in a new tab.
 */

const ENCLOSED_DOCS = [
  "Employee Training Curriculum",
  "HSE Manual Table of Contents",
  "Employee Training Cards",
  "Certificate Of Insurance Details",
  "W9 Documents",
];

const yn = (v) => (v || "").toString().toLowerCase();

// ============================================================================
// PDF styles
// ============================================================================
const s = StyleSheet.create({
  page: {
    padding: 18,
    fontSize: 9.5,
    fontFamily: "Helvetica",
  },
  bold: { fontFamily: "Helvetica-Bold" },

  // Header table
  headerTable: { flexDirection: "row", border: "1px solid #000", marginBottom: 2 },
  logoCell: {
    width: 110,
    borderRight: "1px solid #000",
    justifyContent: "center",
    alignItems: "center",
    padding: 4,
  },
  logoImg: { width: 80, height: 38, objectFit: "contain" },
  headerRight: { flex: 1 },
  hRow: { flexDirection: "row", borderBottom: "1px solid #000", minHeight: 18 },
  hRowLast: { flexDirection: "row", minHeight: 18 },
  hCell: { padding: 3, borderRight: "1px solid #000", justifyContent: "center" },
  hCellLast: { padding: 3, justifyContent: "center" },

  // Form title
  title: {
    textAlign: "center",
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    marginTop: 4,
    marginBottom: 4,
  },

  // Section banner (black bar)
  banner: {
    backgroundColor: "#000",
    color: "#FFF",
    textAlign: "center",
    padding: 3,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    textTransform: "uppercase",
    border: "1px solid #000",
  },

  // Numbered sub-heading row ("1. Subcontractor Information")
  numHead: {
    borderLeft: "1px solid #000",
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    padding: 3,
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
  },

  // Inline-label cell: "Label: value"
  cellRow: { flexDirection: "row" },
  cell: {
    borderLeft: "1px solid #000",
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    padding: 3,
    flexDirection: "row",
    minHeight: 16,
  },
  cellHalf: { width: "50%" },
  cellQtr: { width: "25%" },
  cellThird: { width: "33.33%" },
  cellFull: { width: "100%" },
  cellLabel: { fontFamily: "Helvetica-Bold", fontSize: 8, marginRight: 4 },
  cellValue: { fontSize: 8, flex: 1 },

  // HSE Stats table
  statsTable: { borderLeft: "1px solid #000", borderTop: "1px solid #000" },
  sRow: { flexDirection: "row" },
  sHead: {
    padding: 3,
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    backgroundColor: "#EAEAEA",
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
  },
  sCell: {
    padding: 3,
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    fontSize: 8,
  },
  sLabel: { width: "40%" },
  sYear: { width: "20%", textAlign: "center" },

  // Question row — 50/50 split, taller rows
  qRow: {
    borderLeft: "1px solid #000",
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    padding: 6,
    flexDirection: "row",
    alignItems: "center",
    minHeight: 28,
  },
  qNum: { fontFamily: "Helvetica-Bold", width: 18, fontSize: 9.5 },
  qLeft: { width: "50%", paddingRight: 8 },
  qText: { fontSize: 9.5, lineHeight: 1.25 },
  qRight: { width: "50%", flexDirection: "row", justifyContent: "flex-end", alignItems: "center" },

  // Y/N checkbox
  ynGroup: { flexDirection: "row" },
  ynItem: { flexDirection: "row", alignItems: "center", marginLeft: 10 },
  chk: { width: 8, height: 8, border: "1px solid #000", marginRight: 4 },
  chkOn: { backgroundColor: "#000" },
  ynLabel: { fontSize: 9 },

  // Attestation block
  attest: {
    borderLeft: "1px solid #000",
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    padding: 5,
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    backgroundColor: "#F8F8F8",
    marginTop: 4,
  },
});

// ============================================================================
// PDF primitives
// ============================================================================
const Chk = ({ on }) => <View style={[s.chk, on ? s.chkOn : {}]} />;

const YNPDF = ({ value, extraText = null }) => (
  <View style={s.ynGroup}>
    <View style={s.ynItem}>
      <Chk on={yn(value) === "yes"} />
      <Text style={s.ynLabel}>Yes</Text>
    </View>
    <View style={s.ynItem}>
      <Chk on={yn(value) === "no"} />
      <Text style={s.ynLabel}>No{extraText ? " " + extraText : ""}</Text>
    </View>
  </View>
);

const CellPDF = ({ label, value, style }) => (
  <View style={[s.cell, style]}>
    <Text style={s.cellLabel}>{label}</Text>
    <Text style={s.cellValue}>{value || " "}</Text>
  </View>
);

const QRowPDF = ({ number, text, value, hintText = null, right = null }) => (
  <View style={s.qRow} wrap={false}>
    <Text style={s.qNum}>{number}.</Text>
    <View style={s.qLeft}>
      <Text style={s.qText}>
        {text}
        {hintText ? <Text style={{ color: "#555" }}> {hintText}</Text> : null}
      </Text>
    </View>
    <View style={s.qRight}>
      {right !== null ? right : <YNPDF value={value} />}
    </View>
  </View>
);

// ============================================================================
// PDF Document
// ============================================================================
const SubcontractorPDF = ({ data }) => {
  const ci = data.companyinfo || {};
  const contacts = data.contacts || {};
  const ins = contacts.insurance || {};
  const bids = contacts.requestingBids || {};
  const evalc = contacts.contractorEvaluation || {};
  const hseinfo = data.hseinfo || {};
  const stats = data.hsestats || {};
  const safety = data.safety || {};
  const online = safety.onlineDatabases || {};
  const dat = safety.drugAlcoholTesting;
  const att = data.attestation || {};
  const enclosed = Array.isArray(att.enclosedDocuments) ? att.enclosedDocuments : [];
  const services = Array.isArray(ci.servicesProvided) ? ci.servicesProvided : [];

  const yearKeys = Object.keys(stats.totalManhours || stats.trir || {});
  const sortedYears = [...yearKeys].sort((a, b) => parseInt(b) - parseInt(a));
  while (sortedYears.length < 3) sortedYears.push("");
  const gy = (field, year) => {
    if (!field || !year) return "";
    const v = field[year];
    return v === null || v === undefined ? "" : String(v);
  };

  return (
    <Document>
      {/* ---------- PAGE 1 ---------- */}
      <Page size="A4" style={s.page}>
        {/* Document header */}
        <View style={s.headerTable}>
          <View style={s.logoCell}>
            <Image src={logo} style={s.logoImg} />
          </View>
          <View style={s.headerRight}>
            <View style={s.hRow}>
              <View style={[s.hCell, { width: "40%" }]}>
                <Text style={s.bold}>HEALTH SAFETY & ENVIRONMENTAL</Text>
              </View>
              <View style={[s.hCell, { width: "25%" }]}>
                <Text style={s.bold}>Document Name:</Text>
              </View>
              <View style={[s.hCellLast, { width: "35%" }]}>
                <Text>SUBCONTRACTOR SAFETY PRE-QUALIFICATION FORM</Text>
              </View>
            </View>
            <View style={s.hRow}>
              <View style={[s.hCell, { width: "40%" }]}>
                <Text> </Text>
              </View>
              <View style={[s.hCell, { width: "25%" }]}>
                <Text style={s.bold}>Section:</Text>
              </View>
              <View style={[s.hCellLast, { width: "35%" }]}>
                <Text>HSE-18</Text>
              </View>
            </View>
            <View style={s.hRowLast}>
              <View style={[s.hCell, { width: "20%" }]}>
                <Text><Text style={s.bold}>Revision:</Text> 2</Text>
              </View>
              <View style={[s.hCell, { width: "20%" }]}>
                <Text><Text style={s.bold}>Date:</Text> 05-23-2023</Text>
              </View>
              <View style={[s.hCellLast, { width: "60%" }]}>
                <Text><Text style={s.bold}>Document Number:</Text> 18-7-A</Text>
              </View>
            </View>
          </View>
        </View>

        <Text style={s.title}>Subcontractor Safety Pre-Qualification Form</Text>

        {/* GENERAL INFORMATION */}
        <Text style={s.banner}>General Information</Text>

        {/* 1. Subcontractor Information */}
        <Text style={s.numHead}>1. Subcontractor Information</Text>
        <View style={s.cellRow}>
          <CellPDF label="Subcontractor Name:" value={ci.subcontractorName} style={s.cellHalf} />
          <CellPDF label="Telephone Number:" value={ci.telephoneNumber || data.phone} style={s.cellHalf} />
        </View>
        <View style={s.cellRow}>
          <CellPDF label="Street Address:" value={ci.streetAddress} style={s.cellHalf} />
          <CellPDF label="Fax Number:" value={ci.faxNumber} style={s.cellHalf} />
        </View>
        <View style={s.cellRow}>
          <CellPDF label="City:" value={ci.city} style={s.cellHalf} />
          <CellPDF label="Website Address:" value={ci.websiteAddress} style={s.cellHalf} />
        </View>
        <View style={s.cellRow}>
          <CellPDF label="Province/State:" value={ci.provinceState} style={s.cellHalf} />
          <CellPDF label="Postal Code/Zip:" value={ci.postalCode} style={s.cellHalf} />
        </View>

        {/* 2. Officers */}
        <Text style={s.numHead}>2. Officers</Text>
        <CellPDF label="President:" value={ci.president} style={s.cellFull} />
        <CellPDF label="Vice President:" value={ci.vicePresident} style={s.cellFull} />
        <CellPDF label="Treasurer:" value={ci.treasurer} style={s.cellFull} />

        {/* 3. Services */}
        <CellPDF
          label="3. Services that your organization will be providing to NTACT?"
          value={services.length > 0 ? services.join(", ") : ""}
          style={s.cellFull}
        />

        {/* 4. Insurance */}
        <Text style={s.numHead}>4. Contact for Insurance Information:</Text>
        <View style={s.cellRow}>
          <CellPDF label="Title:" value={ins.title} style={s.cellQtr} />
          <CellPDF label="Telephone:" value={ins.telephone} style={s.cellQtr} />
          <CellPDF label="Fax:" value={ins.fax} style={s.cellQtr} />
          <CellPDF label="Email:" value={ins.email} style={s.cellQtr} />
        </View>

        {/* 5. Requesting bids */}
        <Text style={s.numHead}>5. Contact for requesting bids:</Text>
        <View style={s.cellRow}>
          <CellPDF label="Title:" value={bids.title} style={s.cellQtr} />
          <CellPDF label="Telephone:" value={bids.telephone} style={s.cellQtr} />
          <CellPDF label="Fax:" value={bids.fax} style={s.cellQtr} />
          <CellPDF label="Email:" value={bids.email} style={s.cellQtr} />
        </View>

        {/* 6. Evaluation */}
        <Text style={s.numHead}>6. Contractor Evaluation form completed by:</Text>
        <View style={[s.cellRow, { marginBottom: 4 }]}>
          <CellPDF label="Title:" value={evalc.title} style={s.cellQtr} />
          <CellPDF label="Telephone:" value={evalc.telephone} style={s.cellQtr} />
          <CellPDF label="Fax:" value={evalc.fax} style={s.cellQtr} />
          <CellPDF label="Email:" value={evalc.email} style={s.cellQtr} />
        </View>

        {/* HSE Performance */}
        <Text style={s.banner}>Health, Safety, and Environmental Performance</Text>
        <Text style={s.numHead}>Highest Ranking HSE professional in the Firm:</Text>
        <View style={s.cellRow}>
          <CellPDF label="Name/Title:" value={hseinfo.hseNameTitle} style={s.cellThird} />
          <CellPDF label="Phone:" value={hseinfo.hsePhone} style={s.cellThird} />
          <CellPDF label="Email:" value={hseinfo.hseEmail} style={s.cellThird} />
        </View>

        {/* HSE Stats */}
        <Text style={[s.banner, { marginTop: 4 }]}>Previous Three-Year HSE Statistics</Text>
        <View style={s.statsTable}>
          <View style={s.sRow}>
            <View style={[s.sHead, s.sLabel]}><Text>Health and Safety Incidents</Text></View>
            {sortedYears.map((y, i) => (
              <View key={i} style={[s.sHead, s.sYear]}><Text>{y || "—"}</Text></View>
            ))}
          </View>
          {[
            { key: "totalManhours", label: "Total Manhours" },
            { key: "trir", label: "Total Recordable Incident Rate (TRIR)" },
            { key: "lwc", label: "Lost Workday Cases (LWC)" },
            { key: "lwdr", label: "Lost Workday Incident Rate (LWDR)" },
          ].map((row) => (
            <View key={row.key} style={s.sRow}>
              <View style={[s.sCell, s.sLabel]}><Text style={s.bold}>{row.label}</Text></View>
              {sortedYears.map((y, i) => (
                <View key={i} style={[s.sCell, s.sYear]}><Text>{gy(stats[row.key], y) || " "}</Text></View>
              ))}
            </View>
          ))}
          {(stats.emr !== undefined || stats.fatalities_last_3_years !== undefined || stats.serious_incidents_last_3_years !== undefined) && (
            <>
              <View style={s.sRow}>
                <View style={[s.sCell, s.sLabel]}><Text style={s.bold}>EMR (Experience Modifier Rate)</Text></View>
                <View style={[s.sCell, { width: "60%", textAlign: "center" }]}><Text>{stats.emr || " "}</Text></View>
              </View>
              <View style={s.sRow}>
                <View style={[s.sCell, s.sLabel]}><Text style={s.bold}>Fatalities (Last 3 Years)</Text></View>
                <View style={[s.sCell, { width: "60%", textAlign: "center" }]}><Text>{stats.fatalities_last_3_years || " "}</Text></View>
              </View>
              <View style={s.sRow}>
                <View style={[s.sCell, s.sLabel]}><Text style={s.bold}>Serious Incidents (Last 3 Years)</Text></View>
                <View style={[s.sCell, { width: "60%", textAlign: "center" }]}><Text>{stats.serious_incidents_last_3_years || " "}</Text></View>
              </View>
            </>
          )}
        </View>
      </Page>

      {/* ---------- PAGE 2 ---------- */}
      <Page size="A4" style={s.page}>
        {/* Repeated document header */}
        <View style={s.headerTable}>
          <View style={s.logoCell}>
            <Image src={logo} style={s.logoImg} />
          </View>
          <View style={s.headerRight}>
            <View style={s.hRow}>
              <View style={[s.hCell, { width: "40%" }]}>
                <Text style={s.bold}>HEALTH SAFETY & ENVIRONMENTAL</Text>
              </View>
              <View style={[s.hCell, { width: "25%" }]}>
                <Text style={s.bold}>Document Name:</Text>
              </View>
              <View style={[s.hCellLast, { width: "35%" }]}>
                <Text>SUBCONTRACTOR SAFETY PRE-QUALIFICATION FORM</Text>
              </View>
            </View>
            <View style={s.hRowLast}>
              <View style={[s.hCell, { width: "20%" }]}>
                <Text><Text style={s.bold}>Revision:</Text> 2</Text>
              </View>
              <View style={[s.hCell, { width: "20%" }]}>
                <Text><Text style={s.bold}>Date:</Text> 05-23-2023</Text>
              </View>
              <View style={[s.hCellLast, { width: "60%" }]}>
                <Text><Text style={s.bold}>Section:</Text> HSE-18 &nbsp;&nbsp;<Text style={s.bold}>Document Number:</Text> 18-7-A</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Black banner for safety section */}
        <Text style={[s.banner, { marginTop: 4 }]}>Safety & Compliance Questions</Text>

        <QRowPDF number={1} text="Do you have a company safety program?" value={safety.safetyProgram} hintText="(If No, please explain details in attachment including frequency of safety meetings, inspections, training, reporting methods)" />
        <QRowPDF number={2} text="Do you have a safety manual?" value={safety.safetyManual} hintText="(If Yes, please attach a table of contents)" />

        <QRowPDF number={3} text="Does your company have a drug and Alcohol testing program?" value={dat} />

        {/* Q4 Online databases — 50/50 split */}
        <QRowPDF
          number={4}
          text="Are you currently subscribed to the following online safety databases?"
          hintText="If so, please provide the ID number next to each bullet."
          right={
            <View style={{ width: "100%" }}>
              {[
                ["ISNetworld", "Veriforce"],
                ["NCMS", "TPS"],
                ["Avetta", null],
              ].map(([a, b], i) => (
                <View key={i} style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 2 }}>
                  <View style={{ flexDirection: "row", alignItems: "center", width: "50%" }}>
                    <Text style={{ fontSize: 9, marginRight: 4 }}>{a}:</Text>
                    <YNPDF value={online[a]} />
                  </View>
                  <View style={{ flexDirection: "row", alignItems: "center", width: "50%" }}>
                    {b ? (
                      <>
                        <Text style={{ fontSize: 9, marginRight: 4 }}>{b}:</Text>
                        <YNPDF value={online[b]} />
                      </>
                    ) : <Text> </Text>}
                  </View>
                </View>
              ))}
            </View>
          }
        />

        <QRowPDF number={5} text="Has your company ever been issued an OSHA Citation?" value={safety.oshaCitation} hintText="(If Yes, please explain in an attachment)" />

        <QRowPDF
          number={6}
          text="Do you currently have a third-party medical consortium for case management of injuries of employees? (i.e., Axiom, Extreme md, Occumed)"
          value={safety.thirdPartyMedical}
        />

        <QRowPDF number={7} text="Do you have a Job Safety Analysis (JSA) process in place?" value={safety.jsaProcess} />
        <QRowPDF number={8} text="Is there a Root Cause Analysis process used for investigations, near misses, and environmental spills?" value={safety.rootCauseAnalysis} />
        <QRowPDF number={9} text="Do you have an HSE Orientation Program for new hires and newly hired or promoted supervisors?" value={safety.hseOrientation} hintText="(If Yes, attach training curriculum)" />

        {/* Attestation */}
        <Text style={s.attest}>
          I attest that all information provided in previous pages is accurate according to the company's policies and procedures. The sub-contractor will comply with and adhere to all relevant Health, Safety, and Environmental set forth by NTACT and their clients.
        </Text>

        <CellPDF label="Print Name:" value={att.printName} style={s.cellFull} />
        <CellPDF label="Position:" value={att.position} style={s.cellFull} />

        <View style={[s.cell, s.cellFull, { minHeight: 26 }]} wrap={false}>
          <Text style={s.cellLabel}>Signature:</Text>
          {att.signature && att.signature.startsWith("data:image") ? (
            <Image src={att.signature} style={{ width: 100, height: 22 }} />
          ) : (
            <Text style={s.cellValue}>{att.signature || " "}</Text>
          )}
        </View>

        <CellPDF label="Date:" value={att.attestationDate} style={s.cellFull} />

        {/* Enclosed Documents */}
        <View style={{ marginTop: 6 }} wrap={false}>
          <Text style={s.banner}>Enclosed Documents</Text>
          {ENCLOSED_DOCS.map((doc) => {
            const checked = enclosed.includes(doc);
            return (
              <View key={doc} style={[s.cellRow, { borderLeft: "1px solid #000" }]}>
                <View style={{ width: "85%", padding: 3, borderRight: "1px solid #000", borderBottom: "1px solid #000" }}>
                  <Text style={{ fontSize: 8 }}>{doc}</Text>
                </View>
                <View style={{ width: "15%", padding: 3, borderRight: "1px solid #000", borderBottom: "1px solid #000", alignItems: "center" }}>
                  <Chk on={checked} />
                </View>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

// ============================================================================
// HTML modal view — compact inline-cell style matching the PDF
// ============================================================================
const Banner = ({ children }) => (
  <div className="bg-black text-white text-[12px] font-bold uppercase tracking-wide text-center py-1 border border-black">
    {children}
  </div>
);

const NumHead = ({ children }) => (
  <div className="border-l border-r border-b border-black px-2 py-1 text-[12px] font-bold">
    {children}
  </div>
);

// Inline "Label: value" cell, matching the compact PDF look
const Cell = ({ label, value, className = "" }) => (
  <div className={`border-l border-r border-b border-black px-2 py-1 flex items-center text-[11px] min-h-[22px] ${className}`}>
    <span className="font-bold mr-1 whitespace-nowrap">{label}</span>
    <span className="truncate">{value || "\u00A0"}</span>
  </div>
);

const YNBoxes = ({ value, extra = null }) => {
  const yes = yn(value) === "yes";
  const no = yn(value) === "no";
  return (
    <span className="inline-flex items-center gap-3 text-[11px]">
      <span className="inline-flex items-center gap-1">
        <span className={`inline-block w-2.5 h-2.5 border border-black ${yes ? "bg-black" : "bg-white"}`} />
        Yes
      </span>
      <span className="inline-flex items-center gap-1">
        <span className={`inline-block w-2.5 h-2.5 border border-black ${no ? "bg-black" : "bg-white"}`} />
        No{extra ? " " + extra : ""}
      </span>
    </span>
  );
};

const QRow = ({ number, text, hint, value, right = null }) => (
  <div className="border-l border-r border-b border-black px-3 py-3 flex items-center gap-3 text-[12px] min-h-[42px]">
    <div className="font-bold w-5 shrink-0">{number}.</div>
    <div className="w-1/2 pr-3 leading-snug">
      {text}
      {hint && <span className="text-gray-500 italic"> {hint}</span>}
    </div>
    <div className="w-1/2 flex justify-end items-center">
      {right || <YNBoxes value={value} />}
    </div>
  </div>
);

const SubcontractorDetailModal = ({ data, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [showScorecard, setShowScorecard] = useState(false);
  if (!data) return null;

  const ci = data.companyinfo || {};
  const contacts = data.contacts || {};
  const ins = contacts.insurance || {};
  const bids = contacts.requestingBids || {};
  const evalc = contacts.contractorEvaluation || {};
  const hseinfo = data.hseinfo || {};
  const stats = data.hsestats || {};
  const safety = data.safety || {};
  const online = safety.onlineDatabases || {};
  const dat = safety.drugAlcoholTesting;
  const att = data.attestation || {};
  const enclosed = Array.isArray(att.enclosedDocuments) ? att.enclosedDocuments : [];
  const services = Array.isArray(ci.servicesProvided) ? ci.servicesProvided : [];

  const yearKeys = Object.keys(stats.totalManhours || stats.trir || {});
  const sortedYears = [...yearKeys].sort((a, b) => parseInt(b) - parseInt(a));
  while (sortedYears.length < 3) sortedYears.push("");
  const gy = (field, year) => {
    if (!field || !year) return "";
    const v = field[year];
    return v === null || v === undefined ? "" : String(v);
  };

  const handleGeneratePDF = async () => {
    try {
      setGenerating(true);
      const { pdf } = await import("@react-pdf/renderer");
      const blob = await pdf(<SubcontractorPDF data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      console.error("Error generating subcontractor PDF:", e);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] overflow-y-auto">
      <Backdrop open={generating} sx={{ zIndex: 10000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="min-h-full flex items-start justify-center p-4">
        <div className="bg-white shadow-2xl w-full max-w-4xl my-6">
          {/* Toolbar */}
          <div className="sticky top-0 z-20 bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
            <div className="text-sm font-medium text-gray-700">
              Subcontractor #{data.id} — {data.firstname || ""} {data.lastname || ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleGeneratePDF}
                disabled={generating}
                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                <PrintIcon style={{ fontSize: 16 }} /> Print / PDF
              </button>
              <button
                onClick={onClose}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full p-1.5"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* ========== PAPER ========== */}
          <div className="p-5 text-black" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
            {/* Document header */}
            <table className="w-full border-collapse border border-black text-[10px]">
              <tbody>
                <tr>
                  <td rowSpan={3} className="border border-black p-1 w-[140px] text-center align-middle">
                    <img src={logo} alt="NTACT" className="mx-auto" style={{ maxHeight: 40 }} />
                  </td>
                  <td className="border border-black px-2 py-1 font-bold" colSpan={2}>
                    HEALTH SAFETY &amp; ENVIRONMENTAL
                  </td>
                  <td className="border border-black px-2 py-1 font-bold">Document Name:</td>
                  <td className="border border-black px-2 py-1">
                    SUBCONTRACTOR SAFETY PRE-QUALIFICATION FORM
                  </td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1" colSpan={2}>&nbsp;</td>
                  <td className="border border-black px-2 py-1 font-bold">Section:</td>
                  <td className="border border-black px-2 py-1">HSE-18</td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1 font-bold">Revision:</td>
                  <td className="border border-black px-2 py-1">2</td>
                  <td className="border border-black px-2 py-1 font-bold">Date:</td>
                  <td className="border border-black px-2 py-1" colSpan={2}>
                    05-23-2023 &nbsp;&nbsp;<span className="font-bold">Document Number:</span> 18-7-A
                  </td>
                </tr>
              </tbody>
            </table>

            <h1 className="text-center font-bold text-[14px] uppercase my-2">
              Subcontractor Safety Pre-Qualification Form
            </h1>

            {/* GENERAL INFORMATION */}
            <Banner>General Information</Banner>

            <NumHead>1. Subcontractor Information</NumHead>
            <div className="grid grid-cols-2">
              <Cell label="Subcontractor Name:" value={ci.subcontractorName} />
              <Cell label="Telephone Number:" value={ci.telephoneNumber || data.phone} />
              <Cell label="Street Address:" value={ci.streetAddress} />
              <Cell label="Fax Number:" value={ci.faxNumber} />
              <Cell label="City:" value={ci.city} />
              <Cell label="Website Address:" value={ci.websiteAddress} />
              <Cell label="Province/State:" value={ci.provinceState} />
              <Cell label="Postal Code/Zip:" value={ci.postalCode} />
            </div>

            <NumHead>2. Officers</NumHead>
            <Cell label="President:" value={ci.president} />
            <Cell label="Vice President:" value={ci.vicePresident} />
            <Cell label="Treasurer:" value={ci.treasurer} />

            <Cell
              label="3. Services that your organization will be providing to NTACT?"
              value={services.length > 0 ? services.join(", ") : ""}
            />

            <NumHead>4. Contact for Insurance Information:</NumHead>
            <div className="grid grid-cols-4">
              <Cell label="Title:" value={ins.title} />
              <Cell label="Telephone:" value={ins.telephone} />
              <Cell label="Fax:" value={ins.fax} />
              <Cell label="Email:" value={ins.email} />
            </div>

            <NumHead>5. Contact for requesting bids:</NumHead>
            <div className="grid grid-cols-4">
              <Cell label="Title:" value={bids.title} />
              <Cell label="Telephone:" value={bids.telephone} />
              <Cell label="Fax:" value={bids.fax} />
              <Cell label="Email:" value={bids.email} />
            </div>

            <NumHead>6. Contractor Evaluation form completed by:</NumHead>
            <div className="grid grid-cols-4 mb-3">
              <Cell label="Title:" value={evalc.title} />
              <Cell label="Telephone:" value={evalc.telephone} />
              <Cell label="Fax:" value={evalc.fax} />
              <Cell label="Email:" value={evalc.email} />
            </div>

            <Banner>Health, Safety, and Environmental Performance</Banner>
            <NumHead>Highest Ranking HSE professional in the Firm:</NumHead>
            <div className="grid grid-cols-3">
              <Cell label="Name/Title:" value={hseinfo.hseNameTitle} />
              <Cell label="Phone:" value={hseinfo.hsePhone} />
              <Cell label="Email:" value={hseinfo.hseEmail} />
            </div>

            <div
              onClick={() => data.scorecard && setShowScorecard(true)}
              className={data.scorecard ? "cursor-pointer" : ""}
              title={data.scorecard ? "View Scorecard" : undefined}
            >
              <Banner>
                Previous Three-Year HSE Statistics
                {data.scorecard && (
                  <span className="ml-2 text-[10px] font-normal normal-case opacity-80">
                    (click to view scorecard)
                  </span>
                )}
              </Banner>
            </div>
            <table className="w-full border-collapse text-[11px] mb-3">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black px-2 py-1 text-left font-bold">Health and Safety Incidents</th>
                  {sortedYears.map((y, i) => (
                    <th key={i} className="border border-black px-2 py-1 text-center font-bold">{y || "—"}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "totalManhours", label: "Total Manhours" },
                  { key: "trir", label: "Total Recordable Incident Rate (TRIR)" },
                  { key: "lwc", label: "Lost Workday Cases (LWC)" },
                  { key: "lwdr", label: "Lost Workday Incident Rate (LWDR)" },
                ].map((row) => (
                  <tr key={row.key}>
                    <td className="border border-black px-2 py-1 font-semibold">{row.label}</td>
                    {sortedYears.map((y, i) => (
                      <td key={i} className="border border-black px-2 py-1 text-center">{gy(stats[row.key], y) || "\u00A0"}</td>
                    ))}
                  </tr>
                ))}
                {(stats.emr !== undefined || stats.fatalities_last_3_years !== undefined || stats.serious_incidents_last_3_years !== undefined) && (
                  <>
                    <tr>
                      <td className="border border-black px-2 py-1 font-semibold">EMR (Experience Modifier Rate)</td>
                      <td colSpan={3} className="border border-black px-2 py-1 text-center">{stats.emr || "\u00A0"}</td>
                    </tr>
                    <tr>
                      <td className="border border-black px-2 py-1 font-semibold">Fatalities (Last 3 Years)</td>
                      <td colSpan={3} className="border border-black px-2 py-1 text-center">{stats.fatalities_last_3_years || "\u00A0"}</td>
                    </tr>
                    <tr>
                      <td className="border border-black px-2 py-1 font-semibold">Serious Incidents (Last 3 Years)</td>
                      <td colSpan={3} className="border border-black px-2 py-1 text-center">{stats.serious_incidents_last_3_years || "\u00A0"}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {/* ===== Page 2 — repeat document header then black banner ===== */}
            <div className="mt-4">
              <table className="w-full border-collapse border border-black text-[10px]">
                <tbody>
                  <tr>
                    <td rowSpan={2} className="border border-black p-1 w-[140px] text-center align-middle">
                      <img src={logo} alt="NTACT" className="mx-auto" style={{ maxHeight: 40 }} />
                    </td>
                    <td className="border border-black px-2 py-1 font-bold" colSpan={2}>
                      HEALTH SAFETY &amp; ENVIRONMENTAL
                    </td>
                    <td className="border border-black px-2 py-1 font-bold">Document Name:</td>
                    <td className="border border-black px-2 py-1">
                      SUBCONTRACTOR SAFETY PRE-QUALIFICATION FORM
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black px-2 py-1 font-bold">Revision:</td>
                    <td className="border border-black px-2 py-1">2</td>
                    <td className="border border-black px-2 py-1 font-bold">Section / Date:</td>
                    <td className="border border-black px-2 py-1">
                      HSE-18 &nbsp;·&nbsp; 05-23-2023 &nbsp;·&nbsp;<span className="font-bold">Document Number:</span> 18-7-A
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-2">
              <Banner>Safety &amp; Compliance Questions</Banner>
            </div>

            <QRow number={1} text="Do you have a company safety program?" hint="(If No, please explain details in attachment including frequency of safety meetings, inspections, training, reporting methods)" value={safety.safetyProgram} />
            <QRow number={2} text="Do you have a safety manual?" hint="(If Yes, please attach a table of contents)" value={safety.safetyManual} />

            <QRow number={3} text="Does your company have a drug and Alcohol testing program?" value={dat} />

            {/* Q4 online databases */}
            <QRow
              number={4}
              text={<>Are you currently subscribed to the following online safety databases? <span className="text-gray-500 italic">If so, please provide the ID number next to each bullet.</span></>}
              right={
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 w-full">
                  {["ISNetworld", "Veriforce", "NCMS", "TPS", "Avetta"].map((db) => (
                    <div key={db} className="flex items-center justify-between text-[11px]">
                      <span>{db}:</span>
                      <YNBoxes value={online[db]} />
                    </div>
                  ))}
                </div>
              }
            />

            <QRow number={5} text="Has your company ever been issued an OSHA Citation?" hint="(If Yes, please explain in an attachment)" value={safety.oshaCitation} />

            <QRow
              number={6}
              text="Do you currently have a third-party medical consortium for case management of injuries of employees? (i.e., Axiom, Extreme md, Occumed)"
              value={safety.thirdPartyMedical}
            />

            <QRow number={7} text="Do you have a Job Safety Analysis (JSA) process in place?" value={safety.jsaProcess} />
            <QRow number={8} text="Is there a Root Cause Analysis process used for investigations, near misses, and environmental spills?" value={safety.rootCauseAnalysis} />
            <QRow number={9} text="Do you have an HSE Orientation Program for new hires and newly hired or promoted supervisors?" hint="(If Yes, attach training curriculum)" value={safety.hseOrientation} />

            {/* Attestation */}
            <div className="border-l border-r border-b border-black px-3 py-2 text-[11px] font-bold bg-gray-50 mt-1">
              I attest that all information provided in previous pages is accurate according to the company's policies and procedures. The sub-contractor will comply with and adhere to all relevant Health, Safety, and Environmental set forth by NTACT and their clients.
            </div>

            <Cell label="Print Name:" value={att.printName} />
            <Cell label="Position:" value={att.position} />
            <div className="border-l border-r border-b border-black px-2 py-1 flex items-center text-[11px] min-h-[38px]">
              <span className="font-bold mr-1 whitespace-nowrap">Signature:</span>
              {att.signature && att.signature.startsWith("data:image") ? (
                <img src={att.signature} alt="Signature" className="max-h-[30px] ml-1" />
              ) : (
                <span>{att.signature || "\u00A0"}</span>
              )}
            </div>
            <Cell label="Date:" value={att.attestationDate} />

            {/* Enclosed Documents */}
            <div className="mt-3">
              <Banner>Enclosed Documents</Banner>
              <table className="w-full border-collapse text-[11px]">
                <tbody>
                  {ENCLOSED_DOCS.map((doc) => {
                    const checked = enclosed.includes(doc);
                    return (
                      <tr key={doc}>
                        <td className="border border-black px-3 py-1 w-[85%]">{doc}</td>
                        <td className="border border-black px-3 py-1 text-center">
                          <span className={`inline-block w-3 h-3 border border-black ${checked ? "bg-black" : "bg-white"}`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-300 px-4 py-3 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium rounded-md bg-gray-800 text-white hover:bg-gray-900"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {showScorecard && (
        <ScorecardPopup data={data} onClose={() => setShowScorecard(false)} />
      )}
    </div>
  );
};

export default SubcontractorDetailModal;
